






























































































import WidgetMixins from '../../../../mixins/WidgetMixins';
import Component, { mixins } from 'vue-class-component';
import { currencyFormat, loadWidget } from '@/utils/helpers';
import { Emit, Prop } from 'vue-property-decorator';
import { ITransactions, PayloadState } from '@/types/types';
import timestamp from '@/filters/timestamp';

interface IRow {
  label: string;
  value: string;
  mono?: boolean;
}

// Curated field map — this dialog is shared by commissions, payments, merchants,
// CU transactions and settlement reports, so it renders whichever of these the
// record happens to carry and leaves the rest to "Additional details".
const AMOUNT_FIELDS = ['totalAmount', 'overallTotalAmount', 'amount', 'difference', 'commission', 'charge'];
const DATE_FIELDS = ['createdAt', 'updatedAt', 'postedAt', 'date', 'businessDate'];
const NOISE_FIELDS = ['_id', '__v', 'id', 'password', 'secret', 'meta', 'targetSystemData', 'paymentLogs', 'log'];

const LABELS: { [key: string]: string } = {
  reference: 'Reference',
  thirdPartyRefID: 'Third-party Ref.',
  callerMsisdn: 'Caller MSISDN',
  msisdn: 'MSISDN',
  accountNumber: 'Account Number',
  organizationName: 'Organization',
  organisationName: 'Organization',
  service: 'Service',
  requestType: 'Request Type',
  via: 'Channel',
  network: 'Network',
  currentState: 'Current State',
  status: 'Status',
  paymentRequestStatus: 'Request Status',
  settlementStatus: 'Settlement Status',
  totalAmount: 'Amount',
  overallTotalAmount: 'Amount w/ Charges',
  difference: 'Difference',
  commission: 'Commission',
  charge: 'Charge',
  amount: 'Amount',
  createdAt: 'Created',
  updatedAt: 'Last Updated',
  postedAt: 'Posted',
  date: 'Date',
  businessDate: 'Business Date',
  gtBankTransactionId: 'GTBank Transaction ID',
  providerTransactionId: 'Provider Transaction ID',
  ipAddress: 'IP Address',
  message: 'Message',
};

const SECTION_FIELDS: { [key: string]: string[] } = {
  Transaction: [
    'reference',
    'thirdPartyRefID',
    'requestType',
    'service',
    'via',
    'network',
    'status',
    'currentState',
    'paymentRequestStatus',
    'settlementStatus',
  ],
  Amounts: ['totalAmount', 'overallTotalAmount', 'difference', 'commission', 'charge'],
  Customer: ['callerMsisdn', 'msisdn', 'accountNumber', 'organizationName', 'organisationName'],
  Timeline: ['createdAt', 'updatedAt', 'postedAt', 'date', 'businessDate'],
  System: ['gtBankTransactionId', 'providerTransactionId', 'ipAddress', 'message'],
};

@Component({
  name: 'GetDetails',
  components: {
    SkeletonPreloader: loadWidget('widgets/SkeletonPreloader'),
  },
  filters: {
    currencyFormat,
  },
})
export default class GetDetails extends mixins(WidgetMixins) {
  @Prop({ default: false, required: true }) state!: boolean;
  @Prop({ default: false }) dialogLoading!: boolean;
  @Prop({ default: false }) loading!: boolean;
  @Prop({ default: false }) link!: string;
  @Prop({ default: null }) details!: ITransactions;

  copied: boolean = false;

  get record(): { [key: string]: any } {
    return (this.details as any) || {};
  }

  get reference(): string {
    return this.record.reference || this.record.thirdPartyRefID || '';
  }

  get heroAmount(): number | undefined {
    const field = AMOUNT_FIELDS.find(key => this.record[key] !== undefined && this.record[key] !== null);
    return field ? this.record[field] : undefined;
  }

  get heroStatus(): string {
    return this.record.paymentStatus || this.record.status || this.record.settlementStatus || '';
  }

  formatValue(key: string, value: any): string {
    if (value === null || value === undefined || value === '') return '—';
    if (AMOUNT_FIELDS.includes(key)) return currencyFormat(value);
    if (DATE_FIELDS.includes(key)) {
      // businessDate/date are plain YYYY-MM-DD strings — leave them as-is
      return key === 'businessDate' || key === 'date' ? String(value) : timestamp(value, 'DD/MM/YYYY HH:mm');
    }
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  get sections(): Array<{ title: string; rows: IRow[] }> {
    return Object.keys(SECTION_FIELDS).map(title => ({
      title,
      rows: SECTION_FIELDS[title]
        .filter(key => this.record[key] !== undefined && this.record[key] !== null && this.record[key] !== '')
        .map(key => ({
          label: LABELS[key] || this.humanize(key),
          value: this.formatValue(key, this.record[key]),
          mono: key.toLowerCase().includes('ref') || key.toLowerCase().includes('id'),
        })),
    }));
  }

  // camelCase -> "Camel Case" so uncurated keys stay readable
  humanize(key: string): string {
    const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }

  get extraRows(): IRow[] {
    const curated = Object.keys(SECTION_FIELDS).reduce(
      (all: string[], title) => all.concat(SECTION_FIELDS[title]),
      [],
    );
    return Object.keys(this.record)
      .filter(key => !curated.includes(key) && !NOISE_FIELDS.includes(key) && key !== 'paymentStatus')
      // empty extras are noise — a wall of dashes hides the fields that do carry a value
      .filter(key => {
        const value = this.record[key];
        if (value === null || value === undefined || value === '') return false;
        if (Array.isArray(value) && !value.length) return false;
        return true;
      })
      .map(key => ({
        label: LABELS[key] || this.humanize(key),
        value: this.formatValue(key, this.record[key]),
      }));
  }

  statusClass(status: string): string {
    const value = String(status).toLowerCase();
    if (['success', 'completed', 'posted', 'delivered'].includes(value)) return 'ka-status--success';
    if (['failed', 'cancelled', 'rejected'].includes(value)) return 'ka-status--error';
    if (['pending', 'processing', 'review_pending', 'on_hold'].includes(value)) return 'ka-status--warning';
    return 'ka-status--info';
  }

  copyReference(): void {
    if (!this.reference) return;
    navigator.clipboard?.writeText(this.reference).then(() => {
      this.copied = true;
      setTimeout(() => (this.copied = false), 1500);
    });
  }

  @Emit('actions')
  close(): PayloadState {
    this.copied = false;
    return {
      idx: 'details',
      state: false,
    };
  }
}
